package ai.jessie.web.work

import ai.jessie.web.theme.FieldErrorStyle
import ai.jessie.web.theme.FieldInputStyle
import ai.jessie.web.theme.FieldOptionalStyle
import ai.jessie.web.theme.FieldStyle
import ai.jessie.web.theme.FieldsetStyle
import ai.jessie.web.theme.FormStackStyle
import ai.jessie.web.theme.Grid2Style
import ai.jessie.web.theme.NumInputStyle
import ai.jessie.web.theme.TextAreaStyle
import ai.jessie.web.theme.VisuallyHiddenStyle
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.GeneralKind
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Fieldset
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Legend
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea

// Steps 4–5 and 9–10. Submitting sends the entry; UC-008 has no separate confirmation. The
// buttons live in the slide-over footer and address this form by [formId].
@Composable
internal fun workForm(state: WorkUiState, actions: WorkActions, formId: String) {
    val draft = state.draft ?: return
    val selections = state.selections ?: return
    Form(
        attrs = {
            id(formId)
            addEventListener("submit") {
                it.preventDefault()
                actions.submit()
            }
        }
    ) {
        Fieldset(
            attrs =
                FieldsetStyle.toAttrs {
                    if (state.busy) attr("disabled", "")
                }
        ) {
            Legend(attrs = VisuallyHiddenStyle.toAttrs()) { Text("Arbeitseintrag") }
            Div(attrs = FormStackStyle.toAttrs()) {
                Div(attrs = Grid2Style.toAttrs()) { projectFields(state, actions) }
                Div(attrs = Grid2Style.toAttrs()) { dateFields(state, actions) }
                workDescription(state, actions)
                workSelect(
                    WorkField(
                        "memberId",
                        "Mitglied",
                        "Pflichtfeld",
                        draft.memberId,
                        state.problems["memberId"],
                    ),
                    selections.members.associate { it.id to memberLabel(it.name) },
                ) { value ->
                    actions.change { it.copy(memberId = value) }
                }
            }
        }
    }
}

// Datum and Dauer side by side; the duration keeps the contract's whole minutes.
@Composable
private fun dateFields(state: WorkUiState, actions: WorkActions) {
    val draft = state.draft ?: return
    val selections = state.selections ?: return
    workInput(
        WorkField(
            "workDate",
            "Datum",
            "Pflichtfeld, spätestens heute",
            draft.workDate,
            state.problems["workDate"],
        ),
        InputType.Date,
        FieldInputStyle,
        { attr("max", selections.currentDate) },
    ) { value ->
        actions.change { it.copy(workDate = value) }
    }
    workInput(
        WorkField(
            "durationMinutes",
            "Dauer in ganzen Minuten",
            "Pflichtfeld, 1 bis 1440",
            draft.durationMinutes,
            state.problems["durationMinutes"],
        ),
        InputType.Text,
        NumInputStyle,
        { attr("inputmode", "numeric") },
    ) { value ->
        actions.change { it.copy(durationMinutes = value) }
    }
}

// Step 2, BR-041: only the projects and positions the server offers.
@Composable
private fun projectFields(state: WorkUiState, actions: WorkActions) {
    val draft = state.draft ?: return
    val selections = state.selections ?: return
    // A position belongs to one project, so another project discards the chosen position.
    workSelect(
        WorkField(
            "projectId",
            "Projekt",
            "Pflichtfeld",
            draft.projectId,
            state.problems["projectId"],
        ),
        selections.projects.associate { it.id to it.name },
    ) { value ->
        actions.change {
            if (it.projectId == value) it else it.copy(projectId = value, positionId = "")
        }
    }
    val positions = selections.projects.firstOrNull { it.id == draft.projectId }?.positions
    workSelect(
        WorkField(
            "positionId",
            "Position",
            "Pflichtfeld, nach Wahl des Projekts",
            draft.positionId,
            state.problems["positionId"],
        ),
        positions.orEmpty().associate { it.id to it.name },
    ) { value ->
        actions.change { it.copy(positionId = value) }
    }
}

@Composable
private fun workDescription(state: WorkUiState, actions: WorkActions) {
    val draft = state.draft ?: return
    val field =
        WorkField(
            "description",
            "Beschreibung",
            "optional, höchstens 1000 Zeichen",
            draft.description,
            state.problems["description"],
        )
    workField(field) {
        TextArea(
            value = field.value,
            attrs =
                TextAreaStyle.toAttrs {
                    id(field.id)
                    attr("rows", "4")
                    fieldAttributes(field)
                    onInput { event -> actions.change { it.copy(description = event.value) } }
                },
        )
    }
}

// `name` is the server's field name, so a returned problem marks exactly this control. The label
// text is `label (hint)`; the hint carries the requirement in the quieter suffix style.
private data class WorkField(
    val name: String,
    val label: String,
    val hint: String,
    val value: String,
    val problem: String? = null,
) {
    val id = "work-$name"
}

@Composable
private fun workField(field: WorkField, control: @Composable () -> Unit) {
    Div(attrs = FieldStyle.toAttrs()) {
        Label(forId = field.id) {
            Text("${field.label} ")
            Span(attrs = FieldOptionalStyle.toAttrs()) { Text("(${field.hint})") }
        }
        control()
        field.problem?.let { P(attrs = FieldErrorStyle.toAttrs()) { Text(it) } }
    }
}

@Composable
private fun workInput(
    field: WorkField,
    type: InputType<String>,
    style: CssStyle<GeneralKind>,
    attributes: AttrsScope<*>.() -> Unit,
    change: (String) -> Unit,
) {
    workField(field) {
        Input(
            type,
            attrs =
                style.toAttrs {
                    id(field.id)
                    value(field.value)
                    attributes()
                    fieldAttributes(field)
                    onInput { change(it.value) }
                },
        )
    }
}

@Composable
private fun workSelect(field: WorkField, options: Map<String, String>, change: (String) -> Unit) {
    workField(field) {
        Select(
            attrs =
                FieldInputStyle.toAttrs {
                    id(field.id)
                    fieldAttributes(field)
                    onChange { change(it.value.orEmpty()) }
                }
        ) {
            (mapOf("" to "Bitte wählen") + options).forEach { (value, label) ->
                Option(value, attrs = { if (value == field.value) selected() }) { Text(label) }
            }
        }
    }
}

private fun AttrsScope<*>.fieldAttributes(field: WorkField) {
    attr("name", field.name)
    attr("autocomplete", "off")
    if (field.problem != null) {
        attr("aria-invalid", "true")
        attr("aria-describedby", "work-problems")
    }
}
